import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Divider,
  TextField,
  CircularProgress,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import closeIcon from "../../images/closeIcon.svg";
import searchIcon from "../../images/ui-ic-search.svg";
import { baseRequest } from "../../fetch/basicThunk";
import {
  QUERY_GET_BILLING_SEARCH_PARENTS,
  QUERY_POST_BILLING_LINK_PARENT,
} from "../../fetch/constantQueries";
import { openModal } from "../../redux/modal/modalSlice";

const useStyles = makeStyles({
  dialogContainer: {
    width: "600px",
    minHeight: "420px",
    display: "flex",
    flexDirection: "column",
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "1rem 1.5rem",
    fontWeight: 600,
  },
  divider: {
    backgroundColor: "#e0e0e0",
  },
  dialogContent: {
    padding: "1.5rem",
    display: "flex",
    flexDirection: "column",
    rowGap: "1rem",
  },
  searchField: {
    width: "100%",
  },
  candidateList: {
    maxHeight: "240px",
    overflowY: "auto",
    border: "1px solid #e0e0e0",
    borderRadius: "4px",
  },
  candidateItem: {
    padding: "0.75rem 1rem",
    borderBottom: "1px solid #f0f0f0",
    cursor: "pointer",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    "&:hover": {
      backgroundColor: "#f5f8fa",
    },
  },
  candidateSelected: {
    backgroundColor: "#e8f4fd !important",
    borderLeft: "4px solid #005691",
  },
  candidateInfo: {
    display: "flex",
    flexDirection: "column",
  },
  candidateTitle: {
    fontWeight: 600,
    fontSize: "0.9rem",
    color: "#212121",
  },
  candidateSubtitle: {
    fontSize: "0.8rem",
    color: "#666",
  },
  dialogActions: {
    padding: "1rem 1.5rem",
    display: "flex",
    justifyContent: "flex-end",
    columnGap: "1rem",
  },
  cancelBtn: {
    textTransform: "none",
  },
  confirmBtn: {
    backgroundColor: "#005691",
    color: "#fff",
    textTransform: "none",
    "&:hover": {
      backgroundColor: "#004170",
    },
    "&:disabled": {
      backgroundColor: "#cccccc",
    },
  },
  noData: {
    padding: "1.5rem",
    textAlign: "center",
    color: "#888",
    fontSize: "0.875rem",
  },
});

export default function LinkParentTicketModal({
  open,
  childTicket,
  onClose,
  onLinked,
}) {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const [searchTerm, setSearchTerm] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const searchTimeoutRef = useRef(null);

  useEffect(() => {
    if (open) {
      const initialSearch = childTicket?.parentMspid || "";
      setSearchTerm(initialSearch);
      setSelectedParent(null);
      if (initialSearch.length >= 2) {
        performSearch(initialSearch);
      } else {
        setCandidates([]);
      }
    } else {
      setSearchTerm("");
      setCandidates([]);
      setSelectedParent(null);
    }
  }, [open, childTicket]);

  const performSearch = (term) => {
    if (!term || term.trim().length < 2) {
      setCandidates([]);
      return;
    }

    setLoadingSearch(true);
    baseRequest(
      "GET",
      `${QUERY_GET_BILLING_SEARCH_PARENTS}?term=${encodeURIComponent(term.trim())}`
    )
      .then((response) => {
        setLoadingSearch(false);
        const results = response?.data || [];
        setCandidates(results);
        if (results.length === 1) {
          setSelectedParent(results[0]);
        }
      })
      .catch(() => {
        setLoadingSearch(false);
        setCandidates([]);
      });
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setSelectedParent(null);

    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      performSearch(value);
    }, 350);
  };

  const handleConfirm = () => {
    if (!childTicket?.caseId || !selectedParent?.caseId) {
      return;
    }

    setSubmitting(true);
    const payload = {
      parentCaseId: selectedParent.caseId,
      parentMspid: selectedParent.mspid,
    };

    baseRequest(
      "POST",
      `${QUERY_POST_BILLING_LINK_PARENT}/${childTicket.caseId}/link-parent`,
      payload
    )
      .then(() => {
        setSubmitting(false);
        dispatch(
          openModal({
            type: "success",
            message: t("BillingContent.SuccessLinkParent"),
          })
        );
        if (onLinked) {
          onLinked();
        }
        onClose();
      })
      .catch(() => {
        setSubmitting(false);
        dispatch(
          openModal({
            type: "error",
            message: t("BillingContent.ErrorCircularLink"),
          })
        );
      });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{ square: true }}
      maxWidth="md"
    >
      <div className={classes.dialogContainer}>
        <DialogTitle disableTypography className={classes.dialogTitle}>
          <Typography variant="h6">
            {t("BillingContent.LinkParentModalTitle")}
          </Typography>
          <IconButton size="small" onClick={onClose}>
            <img height={15} src={closeIcon} alt="close" />
          </IconButton>
        </DialogTitle>
        <Divider className={classes.divider} />
        <DialogContent className={classes.dialogContent}>
          <div style={{ fontSize: "0.875rem", color: "#555" }}>
            <span>
              {t("BillingContent.ChildBadge", {
                mspid: childTicket?.mspid || childTicket?.caseNumber || "",
              })}
            </span>
          </div>
          <TextField
            className={classes.searchField}
            variant="outlined"
            size="small"
            placeholder={t("BillingContent.SearchParentPlaceholder")}
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <img
                  src={searchIcon}
                  alt="search"
                  style={{ width: 16, marginRight: 8, opacity: 0.6 }}
                />
              ),
              endAdornment: loadingSearch ? (
                <CircularProgress size={18} />
              ) : null,
            }}
          />
          <div style={{ fontSize: "0.85rem", fontWeight: 600 }}>
            {t("BillingContent.SelectParentToLink")}
          </div>
          <div className={classes.candidateList}>
            {candidates.length > 0 ? (
              candidates.map((item) => {
                const isSelected = selectedParent?.caseId === item.caseId;
                return (
                  <div
                    key={item.caseId}
                    className={`${classes.candidateItem} ${
                      isSelected ? classes.candidateSelected : ""
                    }`}
                    onClick={() => setSelectedParent(item)}
                  >
                    <div className={classes.candidateInfo}>
                      <span className={classes.candidateTitle}>
                        {item.caseNumber || `ID ${item.caseId}`}{" "}
                        {item.mspid ? `• MSP: ${item.mspid}` : ""}
                      </span>
                      <span className={classes.candidateSubtitle}>
                        {item.vehiclePlate ? `${t("Plate")}: ${item.vehiclePlate}` : ""}{" "}
                        {item.supplierName ? `• ${item.supplierName}` : ""}
                      </span>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className={classes.noData}>
                {loadingSearch
                  ? t("Searching")
                  : t("NoDataToDisplay")}
              </div>
            )}
          </div>
        </DialogContent>
        <Divider className={classes.divider} />
        <DialogActions className={classes.dialogActions}>
          <Button
            className={classes.cancelBtn}
            onClick={onClose}
            disabled={submitting}
          >
            {t("BillingContent.ModalButtonCancel")}
          </Button>
          <Button
            className={classes.confirmBtn}
            onClick={handleConfirm}
            disabled={!selectedParent || submitting}
          >
            {submitting ? (
              <CircularProgress size={18} color="inherit" />
            ) : (
              t("BillingContent.BtnConfirmLink")
            )}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
}
